'use client'

import { memo, useMemo } from 'react'
import { IconCheck, IconX } from '@tabler/icons-react'
import clsx from 'clsx'

import { parseDateToDDMMYYYY } from 'shared/utils/date'
import { addTestId } from 'shared/utils/tests'

import { DiscountCode } from '../../types'

import DiscountCodeActionButton from './DiscountCodeActionButton'
import {
  CardActiveState,
  CardMode,
  determineRebateCardActiveState,
  determineRebateCardMode
} from './utils'

import styles from '../../../../components/BenefitCodeCard/BenefitCodeCard.module.scss'

interface DiscountCodeCardProps {
  rebate: DiscountCode
  isCodeAvailable: boolean
  isCodeUsed: boolean
  activeCodeIDs?: string[]
  marketingNotConsent?: boolean
  minimumOrderValueNotAchieved?: boolean
  onClick?: () => void
}

const DiscountCodeCard = memo(
  ({
    rebate,
    isCodeAvailable,
    isCodeUsed,
    marketingNotConsent,
    minimumOrderValueNotAchieved,
    activeCodeIDs = [],
    onClick
  }: DiscountCodeCardProps) => {
    const cardMode = useMemo(
      () => determineRebateCardMode(isCodeAvailable, isCodeUsed),
      [isCodeAvailable, isCodeUsed]
    )

    const cardActiveState = useMemo(
      () => determineRebateCardActiveState(activeCodeIDs, rebate.id),
      [activeCodeIDs, rebate.id]
    )

    const formattedMinimumOrderValue = useMemo(
      () =>
        rebate.minimumOrderValue?.formatted
          .replace('&nbsp;', '')
          .replace(',00', ''),
      [rebate.minimumOrderValue?.formatted]
    )

    const shouldButtonDisplay = useMemo(
      () => onClick && cardActiveState !== CardActiveState.disabled,
      [cardActiveState, onClick]
    )

    const tooltipText = useMemo(() => {
      switch (cardMode) {
        case CardMode.available:
          return 'Możliwy do zrealizowania'
        case CardMode.unavailable:
          if (marketingNotConsent)
            return 'Do zrealizowania po zaznaczeniu zgody marketingowej'
          if (rebate.minimumOrderValue && minimumOrderValueNotAchieved)
            return `Do zrealizowania po przekroczeniu ${formattedMinimumOrderValue} zamówienia`

          return 'Kupon niemożliwy do zrealizowania'
        case CardMode.used:
          return 'Kupon został zrealizowany'
      }
    }, [
      cardMode,
      formattedMinimumOrderValue,
      marketingNotConsent,
      minimumOrderValueNotAchieved,
      rebate.minimumOrderValue
    ])

    const iconClassName = (base: string) =>
      clsx(base, { [styles.used]: cardMode === CardMode.used })

    return (
      <div
        key={rebate.id}
        className={clsx(styles.container, {
          [styles.disabled]: cardActiveState === CardActiveState.disabled
        })}
        {...addTestId('rebate-code-card-container')}
      >
        <div
          className={clsx(styles.valueWrapper, styles[cardMode])}
          {...addTestId('rebate-code-value-wrapper')}
        >
          <div className={clsx(styles.discountValue, styles[cardMode])}>
            -{rebate.value.formatted.replace('&nbsp;', '').replace(',00', '')}
          </div>
          <span className={clsx(styles.usedTooltip, styles[cardMode])}>
            {tooltipText}
          </span>
        </div>
        <div className={styles.contentWrapper}>
          <div
            className={styles.descriptionWrapper}
            {...addTestId('rebate-code-description-wrapper')}
          >
            <div
              className={clsx(styles.descriptionHeader, {
                [styles.used]: cardMode === CardMode.used
              })}
            >
              {rebate.name}
            </div>
            {rebate.code && (
              <p
                className={clsx(styles.descriptionText, {
                  [styles.used]: cardMode === CardMode.used
                })}
              >
                Kod rabatowy: {rebate.code}
              </p>
            )}

            {marketingNotConsent && (
              <p
                className={clsx(styles.descriptionText, {
                  [styles.used]: cardMode === CardMode.used
                })}
              >
                <IconX size={16} className={iconClassName(styles.iconX)} />
                Nie masz zaznaczonej zgody marketingowej
              </p>
            )}

            {rebate.minimumOrderValue && (
              <p
                className={clsx(styles.descriptionText, {
                  [styles.used]: cardMode === CardMode.used
                })}
              >
                {minimumOrderValueNotAchieved ? (
                  <>
                    <IconX size={16} className={iconClassName(styles.iconX)} />
                  </>
                ) : (
                  <IconCheck size={16} className={iconClassName(styles.iconCheck)} />
                )}
                Minimalna wartość zamówienia: {formattedMinimumOrderValue}
              </p>
            )}

            {rebate.activeTo && (
              <p
                className={clsx(styles.descriptionText, {
                  [styles.used]: cardMode === CardMode.used
                })}
              >
                <IconCheck size={16} className={iconClassName(styles.iconCheck)} />
                Kod rabatowy ważny do: {parseDateToDDMMYYYY(rebate.activeTo)}
              </p>
            )}
          </div>
          {shouldButtonDisplay && (
            <div
              className={styles.actionWrapper}
              {...addTestId('rebate-code-actions-wrapper')}
            >
              <DiscountCodeActionButton
                cardMode={cardMode}
                cardActiveState={cardActiveState}
                marketingNotConsent={marketingNotConsent}
                minimumOrderValueNotAchieved={minimumOrderValueNotAchieved}
                rebate={rebate}
                onClick={onClick}
              />
            </div>
          )}
        </div>
      </div>
    )
  }
)

export default DiscountCodeCard
